import { AnyAri } from '@atlassian/ari/any-ari';
import { ThirdPartyConfigsSync as ThirdPartyConfigs } from '@atlassian/rovo-connectors-config/configs-sync';
import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { createClient } from '@atlassian/search-client/base-search-client';
import { Scope as JiraScope } from '@atlassian/search-client/jira/types';
import { type JiraProject, jiraProjectsQuery } from '@atlassian/search-client/project';
import {
	jiraDcProjectFilterOptionsQuery,
	spaceARIEntitiesQuery,
} from '@atlassian/search-client/search-page';
import { type DCSpaceFilterOption } from '@atlassian/search-client/search-page/types';
import { mergeAndDedupeOptions } from '@atlassian/search-common/merge-and-dedupe-options';

import { DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI } from '../../../../../common/constants/default-cloud-graph-space-entity-ati';
import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type { SelectFilterState } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, type ProductKeys1P } from '../../../../../common/constants/product-keys';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

import { generateQueryParamsGetState } from './utils';

const projectIdToARI = (cloudId: string, projectId: string) =>
	`ari:cloud:jira:${cloudId}:project/${projectId}`;

export const searchedProjectLabel: ({
	projectKey,
	name,
}: {
	projectKey: string;
	name: string;
}) => string = ({ projectKey, name }: { projectKey: string; name: string }): string => {
	if (!projectKey) {
		return name;
	}

	const key = `(${projectKey})`;
	if (name.toLowerCase().includes(key.toLowerCase())) {
		return name;
	}

	return `${name} ${key}`;
};

const searchProjectsForSite = async (query: string, config: CloudConfig, cloudId: string) => {
	const searchClient = createClient({ cloudId });

	const searchProjectsResponse = await searchClient.search({
		query,
		resultLimit: 5,
		scopes: [JiraScope.JiraProject],
		sites: [cloudId],
		analytics: {
			identifier: 'search-page',
		},
	});

	const scopes = searchProjectsResponse.response.rawData.scopes;
	const searchedProjects: JiraProject[] =
		scopes.length > 0
			? scopes[0].results.map((projectResult) => ({
					projectId: projectResult.id,
					name: projectResult.name,
					projectKey: projectResult.attributes.projectKey,
					avatar: {
						small:
							'url' in projectResult.attributes.avatar
								? projectResult.attributes.avatar.url
								: undefined,
					},
				}))
			: [];

	// Results carry no cloudId, so attribute them to the site this call was made against.
	return searchedProjects.map((project) => {
		const projectARI = projectIdToARI(cloudId, project.projectId);
		return {
			trackingKey: projectARI,
			value: projectARI,
			label: searchedProjectLabel(project),
			avatarUrl: project.avatar?.small,
			// The ARI names the site, so hydration can query it directly instead of asking every site.
			queryParamValue: projectARI,
			product: config.product,
		};
	});
};

const searchProject = async (query: string, config: CloudConfig) => {
	const product = config.product;
	if (product === ProductKeys.JiraDC || product === ProductKeys.JsmDC) {
		const headers = buildAtlAttributionHeaders({
			cloudId: config.cloudId,
			activationId: config.activationId,
			product: config.primaryProduct,
		});

		const searchProjectsResponse = await jiraDcProjectFilterOptionsQuery({
			variables: {
				query,
				cloudIdARI: `ari:cloud:confluence::site/${config.cloudId}`,
				limit: 5,
				entities: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
				product: ProductKeys.JiraDC,
				integration: config.selectedDatasource?.integrationAri,
				containerTypes: ThirdPartyConfigs.get(product)?.containerTypes,
			},
			aggAbsoluteUrl: config.aggAbsoluteUrl,
			headers,
		});
		return (
			searchProjectsResponse?.data?.search?.search?.edges?.map((edge: DCSpaceFilterOption) => ({
				trackingKey: edge.node.entity.id,
				value: edge.node.entity.id,
				label: searchedProjectLabel({
					projectKey: edge.node.entity.key,
					name: edge.node.entity.displayName,
				}),
				avatarUrl: edge?.node?.entity?.icon?.url,
				queryParamValue: edge.node.entity.id,
				product: config.product,
			})) || []
		);
	}

	const cloudIds = config.selectedCloudIds ?? [];
	const settled = await Promise.allSettled(
		cloudIds.map((cloudId) => searchProjectsForSite(query, config, cloudId)),
	);

	const succeeded = settled.filter(
		(result): result is PromiseFulfilledResult<Awaited<ReturnType<typeof searchProjectsForSite>>> =>
			result.status === 'fulfilled',
	);

	// A failing site degrades the list, but if every site failed surface the error rather than
	// showing an empty filter as though there were no projects.
	if (!succeeded.length && settled.length) {
		throw (settled[0] as PromiseRejectedResult).reason;
	}

	return mergeAndDedupeOptions(succeeded.map((result) => result.value));
};

const productKeys1P: ProductKeys1P[] = [
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
];

const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('project');

export const filter: Readonly<{
	id: 'project';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'project'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'project'>;
	getInitialState: (
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'project'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'project',
	products: [...productKeys1P, ...products3PFromConfig],
	alwaysVisible: true,
	universal: false,
	queryParams: {
		key: 'jProject',
		fetcher: async (queryParams, config) => {
			if (config.product === ProductKeys.JiraDC || config.product === ProductKeys.JsmDC) {
				const initialProjectsResponse = await spaceARIEntitiesQuery({
					variables: {
						ids: queryParams,
					},
				});

				const projectOptions = initialProjectsResponse.data?.external_entities?.space?.map(
					(space) => ({
						trackingKey: space.id,
						value: space.id,
						label: space.displayName,
						avatarUrl: space.icon?.url || '',
						queryParamValue: space.id,
						product: config.product,
					}),
				);

				return projectOptions || [];
			} else {
				// Query params hold ARIs, which name their own site. Links predating that hold bare
				// project ids and still have to be looked up on every selected site.
				// TODO QS-10359: drop the bare id branch, and the fan-out it forces, once pre-ARI
				// links have aged out. This outlives the serveFpsMultiSiteSearchExperiment cleanup.
				const cloudIds = config.selectedCloudIds ?? [];
				const initialProjectARIs = queryParams.flatMap((queryParamValue) =>
					AnyAri.check(queryParamValue)
						? [queryParamValue]
						: cloudIds.map((cloudId) => projectIdToARI(cloudId, queryParamValue)),
				);
				const headers = buildAtlAttributionHeaders({
					cloudId: config.cloudId,
					activationId: config.activationId,
					product: config.primaryProduct,
				});
				const initialProjectsResponse = await jiraProjectsQuery(initialProjectARIs, headers);
				const initialProjects: JiraProject[] =
					initialProjectsResponse.data?.jira.jiraProjects ?? [];

				// The returned ARI names the site the project actually lives on. Without it a project
				// cannot be attributed, and guessing the current site would select the wrong project.
				const requested = new Set(queryParams);
				return initialProjects.flatMap((project) =>
					project.id && AnyAri.check(project.id)
						? [
								{
									trackingKey: project.id,
									value: project.id,
									label: project.name,
									avatarUrl: project.avatar?.small,
									// Echo back whichever form the URL used so the option stays selected.
									queryParamValue: requested.has(project.id) ? project.id : project.projectId,
									product: config.product,
								},
							]
						: [],
				);
			}
		},
		generateQueryParamsGetState,
	},
	initialOptions: {
		fetcher: async (config) => {
			return searchProject('', config);
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			return searchProject(query, config);
		},
	},
	isSuggestedFilter: (primaryProduct) =>
		primaryProduct === ProductKeys.Jira ||
		primaryProduct === ProductKeys.JiraDC ||
		primaryProduct === ProductKeys.JsmDC ||
		primaryProduct === ProductKeys.JiraServiceManagement ||
		primaryProduct === ProductKeys.JiraProductDiscovery,
});
